import { LibraryFactory } from 'sharebase/factories';
import { AclService } from 'phoenix/services';
import { 
    pxItemTypeGuards,
    Acl,
    ShareBaseItem,
    ShareBaseFolder
} from 'phoenix/domain';

/**
 * Indicates the type of the ref value for @see DirectoryItem objects.
 */
export enum ItemType {
    Library = 'Library',
    Folder = 'Folder',
    Meta = 'meta'
}
/**
 * Reference types for the special items.
 */
export enum MetaRef {
    Root = 'root',
    MyFiles = 'myFiles',
    SharedContent = 'sharedContent'
};

/**
 * Wrapper around sharebase content items (i.e. library, folder) that provides
 * extra information for navigating the items as a directory tree.
 */
export interface DirectoryItem {
    /**
     * A human friendly name for the item.
     */
    name: string;
    /**
     * Reference to the original item. For libraries, it will be the library object.
     * For folders, it will be the folder object. For special items, it will be some
     * representational string.
     */
    ref: any;
    /**
     * Children of the current item.
     */
    children: DirectoryItem[];
    /**
     * Use to easily determine if the item represents a library, a folder, or a special
     * directory item.
     */
    itemType: ItemType;
    /**
     * An optional user acl that is associated with the current library or folder.
     * Used to determine if the user can actually move items to the selected destination.
     */
    acl?: Acl;
}



/**
 * Special DirectoryItem that represents the root of the directory tree.
 * This item does not represent a ShareBase library or folder but is instead
 * a special object maintained by the directory navigation service.
 */
const root: DirectoryItem = {
    name: '',
    ref: MetaRef.Root,
    children: null,
    itemType: ItemType.Meta
};

/**
 * Interface for @see sbDirectoryNavigationService that can be imported
 * in other TypeScript files.
 */
export interface IDirectoryNavigationService {
    /**
     * The directory item (i.e. folder or library) that is currently being viewed.
     */
    current: DirectoryItem;
    /**
     * Returns true if @see current points to the root element in the tree.
     */
    isRoot: boolean;
    /**
     * Navigate to the previous directory item in the path.
     */
    back: () => void;
    /**
     * Navigate to a directory (i.e. folder or library).
     */
    goTo: (item: DirectoryItem) => void;
    /**
     * Force the service to navigate to the root element in the tree.
     */
    goToRoot: () => void;
    /**
     * Adds a child element to the current directory item.
     */
    addChild: (item: ShareBaseFolder) => DirectoryItem;
    /**
     * An ordered collection of @see DirectoryItem objects that represent the
     * path from the root of the directory tree to the item that @see current
     * points to.
     */
    path: DirectoryItem[];

    /**
     * Initialize the directory tree. It's recommended to call this before each new
     * use of the service. This will ensure a stale directory tree is not used.
     */
    init: (item: ShareBaseItem) => ng.IPromise<any>;
    isInitializing: boolean;
}

// Factory function that creates a DirectoryItem object from a ShareBase folder.
const dirItemFromFolder = (folder: any): DirectoryItem => ({
    name: folder.FolderName,
    ref: folder,
    children: null,
    itemType: ItemType.Folder
});

// Factory function that creates a DirectoryItem object from a ShareBase library.
const dirItemFromLibrary = (library: any): DirectoryItem => ({
    name: library.LibraryName,
    ref: library,
    children: null,
    itemType: ItemType.Library
});

export function DirectoryNavigationServiceFactory(
    $q:ng.IQService, 
    $http:ng.IHttpService,
    sbDocumentService,
    sbLibraryService, 
    sbFolderService, 
    sbSharedContentService, 
    sbLibraryFactory:LibraryFactory,
    pxAclService:AclService,
    gettextCatalog:ng.gettext.gettextCatalog
): IDirectoryNavigationService {

    let directory:DirectoryItem;
    let navigationStack: DirectoryItem[];

    // cache current diretory item. This way if a view bindes to the
    // current property, the item will only need to be calculated when
    // the navigation stack has changed. This will prevent angular chnage
    // detection from continuously run the process for finding the current item.
    let _currentItem: DirectoryItem;
    let _currentItemIndex: number;
    let _initializing = true;

    return {
        /**
         * @ngdoc property
         * @name sbDirectoryNavigationService#current
         * @propertyOf sharebase.sbDirectoryNavigationService
         * @kind property
         *
         * @description
         * Get the folder that is currently displayed.
         *
         * @returns {NavFolder} The current navigation folder.
         */
        get current(): DirectoryItem {
            let currentIndex = navigationStack.length - 1;
            if (!_currentItem || currentIndex !== _currentItemIndex) {
                _currentItemIndex = currentIndex;
                _currentItem = navigationStack[_currentItemIndex];
            }
            return _currentItem;
        },
        /**
         * @ngdoc property
         * @name sbDirectoryNavigationService#isRoot
         * @propertyOf sharebase.sbDirectoryNavigationService
         * @kind property
         *
         * @description
         * Check if @see sbDirectoryNavigationService#current is the root item in the
         * directory tree.
         *
         * @returns {Boolean} True if @see sbDirectoryNavigationService#current is the
         * root item and false if it is not.
         */
        get isRoot(): boolean {
            return this.current.ref === MetaRef.Root;
        },
        /**
         * @ngdoc method
         * @name sbDirectoryNavigationService#back
         * @methodOf sharebase.sbDirectoryNavigationService
         * @kind function
         *
         * @description
         * Navigate to the last visited folder. If @see sbDirectoryNavigationService#current
         * points to the root item, then this method will not do anything.
         */
        back,
        /**
         * @ngdoc method
         * @name sbDirectoryNavigationService#goTo
         * @methodOf sharebase.sbDirectoryNavigationService
         * @kind function
         *
         * @description
         * Navigate to a specific folder that is wrapped in an instance
         * of @see NavFolder. The target folder must be a child of the
         * current folder, if it is not, this method will throw.
         *
         * @param {NavFolder} The item to navigate to.
         */
        goTo,
        /**
         * @ngdoc method
         * @name sbDirectoryNavigationService#goToRoot
         * @methodOf sharebase.sbDirectoryNavigationService
         * @kind function
         *
         * @description
         * Forces the navigation service to go back to the root item in the
         * directory tree.
         */
        goToRoot: function goToRoot(): void {
            while (!this.isRoot) {
                this.back();
            }
        },
        /**
         * @ngdoc property
         * @name sbDirectoryNavigationService#path
         * @propertyOf sharebase.sbDirectoryNavigationService
         * @kind property
         *
         * @description
         * Get the path from the root of the directory tree to the current item (@see sbDirectoryNavigationService#current).
         *
         * @returns {DirectoryItem[]} Returns an array of @see DirectoryItem objects.
         */
        get path(): DirectoryItem[] {
            return navigationStack;
        },

        addChild,
        /**
         * @ngdoc method
         * @name sbDirectoryNavigationService#init
         * @methodOf sharebase.sbDirectoryNavigationService
         * @kind method
         *
         * @description
         * Initialize the service. Should call before each new use to eliminate any stale data.
         * @param {StartElement} The item to that is being moved.
         */
        init,
        get isInitializing(): boolean { return _initializing; }
    };

    function init(item:ShareBaseItem): ng.IPromise<any> {
        if (Array.isArray(item)){
            item = item[0];
        }

        _initializing = true;
        
        // Setup initial directory items, all content should be listed
        // under either 'My Files' or 'Shared Content', similar to how
        // the main app navigation works.
        directory = angular.copy<DirectoryItem>(root, { } as any);
        directory.children = [
            {
                name: gettextCatalog.getString('My Files'),
                ref: MetaRef.MyFiles,
                itemType: ItemType.Meta,
                children: null
            },
            {
                name: gettextCatalog.getString('Shared Content'),
                ref: MetaRef.SharedContent,
                itemType: ItemType.Meta,
                children: null
            }
        ];

        let ancestors: ShareBaseItem[];
        let promise:ng.IPromise<any>;
        if (item.Embedded.Path !== null){
            ancestors = item.Embedded.Path;
            promise = $q.resolve();
        } else if (pxItemTypeGuards.isShareBaseFolder(item)){
            promise = sbFolderService.getFolder(item.FolderId);
        } else if (pxItemTypeGuards.isShareBaseDocument(item)){
            promise = sbDocumentService.getDocument(item.DocumentId);
        } else {
            throw new Error('Error initializing directory navigation');
        }

        return promise.then((response) => {
            if (!angular.isDefined(ancestors)){
                if (angular.isDefined(response.Embedded.Document)){
                    response = response.Embedded.Document;
                }
                ancestors = response.Embedded.Path;
            }

            let navItems = ancestors.filter(ancestor => pxItemTypeGuards.isShareBaseFolder(ancestor))
                                        .map(dirItemFromFolder);
        

            // Stack that will be used to navigate the tree of directory items.
            // the top item in the stack represents the current level the user
            // is on. When the user moves forward, the chosen directory will
            // be pushed onto the stack, and when the navigate backwards it
            // will be popped off the stack
            navigationStack = [directory];

            if (!pxItemTypeGuards.isShareBaseFolder(ancestors[0])){
                navigationStack.push(directory.children[0]);
            } else {
                navigationStack.push(directory.children[1]);
            }

            navigationStack = navigationStack.concat(navItems);
    
            _currentItemIndex = navigationStack.length - 1;
            _currentItem = navigationStack[_currentItemIndex];
    
            let promises = navigationStack.map(navItem => {
                return getItemPermissions(navItem)
                    .then(acl => {
                        navItem.acl = acl;
                        return getItemChildren(navItem);
                    })
                    .then(children => {
                        navItem.children = children;
                        return navItem;
                    });
            });

            return $q.all(promises);
        }).finally(() => _initializing = false);
    }

    function addChild(item:ShareBaseFolder): DirectoryItem{
        let folder = [item].map(dirItemFromFolder)[0];
        _currentItem.children.push(folder);
        return folder;
    }

    // navigate backwards one step
    function back(): void {
        // can only go back if the current item is not the root item.
        if (_currentItem.ref !== MetaRef.Root) {
            // the current item is the last one, so just pop it off
            // then set the cached item to the correct item in the stack
            navigationStack.pop();
            _currentItemIndex = navigationStack.length - 1;
            _currentItem = navigationStack[_currentItemIndex];
        }

    }

    // Navigate to a directory item, make sure the item is a child of the current
    // item, get its children, then add it to the navigation stack.
    function goTo(item: DirectoryItem): ng.IPromise<DirectoryItem> {
        // check if the item is a child of the current item, if not throw
        let child: DirectoryItem = null;
        for (let i = 0; i < _currentItem.children.length && child === null; ++i) {
            if (typeof item.ref === 'string' && item.ref === _currentItem.children[i].ref) {
                child = _currentItem.children[i];
            } else if (item.ref.FolderId === _currentItem.children[i].ref.FolderId) {
                child = _currentItem.children[i];
            }
        }
        if (!child) {
            throw new Error('Can only navigate to children of the current folder');
        }

        navigationStack.push(item);
        _currentItemIndex = navigationStack.length - 1;
        _currentItem = navigationStack[_currentItemIndex];

        return getItemPermissions(_currentItem)
            .then(acl => {
                _currentItem.acl = acl;
                return getItemChildren(_currentItem);
            })
            .then(children => {
                _currentItem.children = children;
                return _currentItem;
            });
    }

    function getItemChildren(item: DirectoryItem): ng.IPromise<DirectoryItem[]> {
        // if the item already has children, just return the item's children
        if (item.children && item.children.length) {
            return $q.resolve(item.children);
        }

        if (item.ref === MetaRef.MyFiles) {
            // The "myFiles" directory represents the user's personal library
            return sbLibraryService.getUserLibrary()
                .then(sbLibraryFactory.create)
                .then(lib => sbLibraryService.getLibraryFolders(lib))
                .then(folders => folders.map(dirItemFromFolder));
        } else if (item.ref === MetaRef.SharedContent) {
            // The sharedContent directory represents the root for all content
            // that was shared with the user
            return sbSharedContentService.getContent()
                .then(shareObj => [
                    ...shareObj.libraries
                        .map(sbLibraryFactory.create)
                        .filter(lib => !lib.IsPrivate)
                        .map(dirItemFromLibrary),

                    ...shareObj.folders
                        .map(dirItemFromFolder)
                ]);
        } else if (item.itemType === ItemType.Library) {
            return sbLibraryService.getLibraryFolders(item.ref)
                .then(folders => folders.map(dirItemFromFolder));
        } else if (item.itemType === ItemType.Folder) {
            return sbFolderService.getSubFolders(item.ref)
                .then(folders => folders.map(dirItemFromFolder));
        }

        // some invalid case, just return empty array
        return $q.resolve([]);
    }

    function getItemPermissions(item: DirectoryItem): ng.IPromise<Acl> {
        // just return item if it already has an acl object
        if (item.itemType === ItemType.Meta || item.acl) {
            return $q.resolve(item.acl);
        }

        let url = '';
        if (pxItemTypeGuards.isShareBaseLibrary(_currentItem.ref)) {
            url = `${item.ref.Links.SelfUri}/permissionsforuser`;
        } else {
            url = `${item.ref.Links.Self}/permissionsforuser`;
        }

        return $http.get(url)
            .then(res => pxAclService.create(res.data[0]));
    }
}

DirectoryNavigationServiceFactory.$inject =  [
    '$q',
    '$http',
    'sbDocumentService',
    'sbLibraryService',
    'sbFolderService',
    'sbSharedContentService',
    'sbLibraryFactory',
    'pxAclService',
    'gettextCatalog'
];



// WEBPACK FOOTER //
// ./src/sharebase/services/directoryNavigationService.ts